import { type Color, SearchOutlineIcon } from '@carboncopyco/icons';
import { ColorHex, type InputTextProps } from '@carboncopyco/ui';
import { useClickOutside } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { AnimatePresence, motion } from 'framer-motion';
import { SearchBoxInput } from '../SearchBoxInput';

export interface SearchBoxButtonProps
  extends Pick<InputTextProps, 'autoFocus' | 'placeholder' | 'value'> {
  onChange?: (value: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  'aria-label'?: string;
  buttonColor?: Color;
}

export const SearchBoxButton = ({
  onChange,
  onOpen,
  onClose,
  value,
  buttonColor,
  'aria-label': ariaLabel,
}: SearchBoxButtonProps) => {
  const [isExpanded, setIsExpanded] = useBoolean(false);

  const onCloseHandler = () => {
    setIsExpanded.setFalse();
    onClose?.();
  };
  const onOpenHandler = () => {
    setIsExpanded.setTrue();
    onOpen?.();
  };

  const wrapperRef = useClickOutside(() => !value && onCloseHandler(), [value]);

  return (
    <div className="flex w-full justify-end min-w-0">
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            ref={wrapperRef}
            className="w-full flex-grow"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            key="searchBoxInput"
            variants={{
              open: {
                transform: 'scaleX(1)',
                transformOrigin: 'right',
              },
              collapsed: { transform: 'scaleX(0)' },
            }}
            transition={{
              duration: 0.15,
              ease: 'easeInOut',
            }}
          >
            <SearchBoxInput
              autoFocus
              value={value}
              onChange={onChange}
              onKeyUp={(e) => {
                if (e.code === 'Escape') {
                  onChange?.('');
                  onCloseHandler();
                }
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      {!isExpanded && (
        <div
          className="h-ds-md-input w-5 flex items-center cursor-pointer"
          aria-label={ariaLabel}
          onClick={onOpenHandler}
        >
          <SearchOutlineIcon
            color={buttonColor ?? ColorHex.natural300}
            size={20}
          />
        </div>
      )}
    </div>
  );
};
